import { FormikProvider, useFormik } from 'formik';
import { FormHelper } from '../../../../../helpers/formHelper';
import { CountryCheckHelper } from '../../../../../helpers/countryCheckHelper';
import { useEffect, useRef, useState } from 'react';
import { BorrowerService } from '../../../borrowerService';
import { Box, Chip, TextField } from '@mui/material';
import { OTPAuthenticationDialog } from '../../../../../shared/components/otpAuthenticationDialog';
import { OTPTypes } from '../../../../../constants/otpTypes';
import { SystemSettingsService } from '../../../../../services/systemSettingsService';
import { ConfirmPasswordDialog } from '../../../../../shared/components/confirmPasswordDialog';
import { SectionWithHeading } from '../../../../../shared/components/common/sectionWithHeading';
import { DialogWrapper } from '../../../../../shared/components/common/dialogWrapper';
import { useTranslation } from 'react-i18next';
import { InputWrapper } from '../../../../../shared/components/formUtilities/useInput';
import SelectWrapper from '../../../../../shared/components/formUtilities/useSelect';
import { useSelector } from 'react-redux';
import { ButtonWrapper } from '../../../../../shared/components/formUtilities/useButton';
import { FsButton } from '../../../../../shared/components/button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { UploadIBANDocument } from '../../../../../shared/components/uploadIbanDocumentDialog';
import { UserDocumentTypes } from '../../../../../constants/userDocuments';

export const BorrowerBankDetails = () => {
	const [loading, setLoading] = useState(false);
	const [showOTPAuth, setShowOTPAuth] = useState(false);
	const [isEditInfo, setIsEditInfo] = useState(false);
	const [showIbanDialog, setShowIbanDialog] = useState(false);
	const [ibanInput, setIbanInput] = useState('');
	const [ibanLoading, setIbanLoading] = useState(false);
	const [showIbanDocumentDialog, setShowIbanDocumentDialog] = useState(false);
	const [ibanRequestId, setIbanRequestId] = useState(null);
	const borrowerService = new BorrowerService();
	const systemSettingsService = new SystemSettingsService();
	const [bankDetails, setBankDetails] = useState(null);
	const pendingEditsRef = useRef(null);

	const { borrowerCollections } = useSelector((state) => state.borrowerCollectionReducer);
	const bankCountries = borrowerCollections?.countryCollection || [];
	const { t } = useTranslation();
	const [showPasswordDialog, setShowPasswordDialog] = useState(false);
	const form = useFormik({
		initialValues: {
			accountName: null,
			bankName: null,
			bankCountry: null,
			iban: null,
			accountNumber: null,
			swiftCode: null,
			routingNumber: null,
		},
		...FormHelper.formikProps(),
	});

	const settings = () => {
		return systemSettingsService.systemSettings();
	};

	useEffect(() => {
		getBankDetails();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getBankDetails = (pendingEdits = null) => {
		setLoading(true);
		borrowerService
			.getBankDetails()
			.then((resp) => {
				setBankDetails(resp.data);
				let details = {
					...resp.data,
					iban: resp.data?.ibanRequest?.iban ?? null,
					bankCountryId: resp.data?.bankCountry?.id || null,
				};
				if (pendingEdits) {
					details = { ...details, ...pendingEdits };
				}
				form.setValues(details);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const uploadUserBankDetails = () => {
		setLoading(true);
		const userBankDetails = form.values;
		if (userBankDetails.iban) {
			userBankDetails.iban = userBankDetails.iban.replace(/\s+/g, '');
		}
		delete userBankDetails?.bankCountry;
		borrowerService
			.updateBankDetails(userBankDetails)
			.then(() => {
				setLoading(false);
				setIsEditInfo(false);
				getBankDetails();
			})
			.catch(() => {
				setLoading(false);
			});
	};

	const onBankDetailsSave = () => {
		if (settings()?.enabledOtp) {
			setShowOTPAuth(true);
		} else {
			setShowPasswordDialog(true);
		}
	};

	const onPasswordDialogClose = (e) => {
		setShowPasswordDialog(false);
		if (e) {
			uploadUserBankDetails();
		} else {
			setLoading(false);
		}
	};

	const onCloseOTPAuth = (resp) => {
		setShowOTPAuth(false);
		if (resp) {
			uploadUserBankDetails();
		}
	};

	const submitIbanUpdate = () => {
		setIbanLoading(true);
		pendingEditsRef.current = isEditInfo
			? {
					accountName: form.values.accountName,
					bankName: form.values.bankName,
					bankCountryId: form.values.bankCountryId,
					accountNumber: form.values.accountNumber,
					swiftCode: form.values.swiftCode,
					routingNumber: form.values.routingNumber,
				}
			: null;
		borrowerService
			.updateIban({ iban: ibanInput.replace(/\s+/g, '') })
			.then((resp) => {
				setIbanLoading(false);
				setShowIbanDialog(false);
				if (resp.data?.documentRequested) {
					setIbanRequestId(resp.data.ibanRequestId);
					setShowIbanDocumentDialog(true);
				} else {
					getBankDetails(pendingEditsRef.current);
					pendingEditsRef.current = null;
				}
			})
			.catch(() => {
				setIbanLoading(false);
				pendingEditsRef.current = null;
			});
	};

	const getIbanStatus = () => {
		const req = bankDetails?.ibanRequest;
		if (!req || !req.iban) return null;
		if (req.approved) return { label: t('ibanStatusApproved'), color: 'success' };
		if (req.documentRequested) return { label: t('ibanStatusDocumentRequired'), color: 'warning' };
		return { label: t('ibanStatusPendingApproval'), color: 'info' };
	};

	const onEdit = (e) => {
		e.preventDefault();
		if (isEditInfo) {
			onBankDetailsSave();
		} else {
			setIsEditInfo(!isEditInfo);
		}
	};

	return (
		<Box>
			<SectionWithHeading
				loading={loading}
				isEditButton={true}
				onEditInfo={(e) => {
					onEdit(e);
				}}
				isEditToggle={isEditInfo}
				heading={
					<Box className="flex items-center">
						<span className="mx-2">{t('bankDetails')}</span>
					</Box>
				}
				children={
					<FormikProvider value={form}>
						<Box className="grid grid-flow-row gap-3">
							<Box className="grid grid-cols-1 md:grid-cols-2 gap-3">
								<fieldset disabled={!isEditInfo} style={{ display: 'contents' }}>
									<InputWrapper
										disabled={!!bankDetails?.accountName}
										key={'accountName'}
										name={'accountName'}
										label={'accountName'}
									></InputWrapper>
									<InputWrapper
										key={'bankName'}
										name={'bankName'}
										label={'bankName'}
									></InputWrapper>
									<SelectWrapper
										name={'bankCountryId'}
										key={'bankCountryId'}
										items={bankCountries}
										label={'bankCountry'}
									/>
								</fieldset>
								<Box className="flex items-start gap-2">
									<Box className="flex-1 flex flex-col gap-1">
										<InputWrapper
											key={'iban'}
											name={'iban'}
											label={'iban'}
											disabled={true}
										/>
										{getIbanStatus() && (
											<Chip
												size="small"
												label={getIbanStatus().label}
												color={getIbanStatus().color}
												className="!w-fit"
											/>
										)}
										{!bankDetails?.ibanRequest?.approved &&
											bankDetails?.ibanRequest?.statusChangeReason && (
												<span className="text-xs text-gray-500">
													{t('ibanRejectionReason')}: {bankDetails.ibanRequest.statusChangeReason}
												</span>
											)}
									</Box>
									<ButtonWrapper
										variant={'outlined'}
										classList="!mt-[20px] !min-w-[40px] !w-[40px] !h-[40px]"
										label={<EditOutlinedIcon />}
										onClick={() => {
											setIbanInput(form.values.iban || '');
											setShowIbanDialog(true);
										}}
									/>
									{bankDetails?.ibanRequest?.documentRequested && (
										<ButtonWrapper
											variant={'outlined'}
											classList="!mt-[20px] !min-w-[40px] !w-[40px] !h-[40px]"
											label={<CloudUploadOutlinedIcon />}
											onClick={() => {
												pendingEditsRef.current = isEditInfo
													? {
															accountName: form.values.accountName,
															bankName: form.values.bankName,
															bankCountryId: form.values.bankCountryId,
															accountNumber: form.values.accountNumber,
															swiftCode: form.values.swiftCode,
															routingNumber: form.values.routingNumber,
														}
													: null;
												setIbanRequestId(bankDetails.ibanRequest.ibanRequestId);
												setShowIbanDocumentDialog(true);
											}}
										/>
									)}
								</Box>
								<fieldset disabled={!isEditInfo} style={{ display: 'contents' }}>
									<InputWrapper
										key={'accountNumber'}
										name={'accountNumber'}
										label={'accountNumber'}
										onChange={(e) => {
											const cleanValue = e.target.value.replace(/\s+/g, '');
											form.setFieldValue('accountNumber', cleanValue);
										}}
									></InputWrapper>
									<InputWrapper
										key={'swiftCode'}
										name={'swiftCode'}
										label={'swiftCode'}
										onChange={(e) => {
											const cleanValue = e.target.value.replace(/\s+/g, '');
											form.setFieldValue('swiftCode', cleanValue);
										}}
									></InputWrapper>
									{CountryCheckHelper.ifUnitedStates(form.values?.bankCountryId, bankCountries) && (
										<InputWrapper
											key={'routingNumber'}
											name={'routingNumber'}
											label={'routingNumber'}
											onChange={(e) => {
												const cleanValue = e.target.value.replace(/\s+/g, '');
												form.setFieldValue('routingNumber', cleanValue);
											}}
										></InputWrapper>
									)}
								</fieldset>
							</Box>
						</Box>
					</FormikProvider>
				}
			></SectionWithHeading>
			{showOTPAuth ? (
				<OTPAuthenticationDialog
					type={OTPTypes.Transaction}
					state={showOTPAuth}
					corrolationId={null}
					onClose={(e) => {
						onCloseOTPAuth(e);
					}}
				></OTPAuthenticationDialog>
			) : null}
			{showPasswordDialog ? (
				<ConfirmPasswordDialog
					state={showPasswordDialog}
					onClose={(e) => {
						onPasswordDialogClose(e);
					}}
				></ConfirmPasswordDialog>
			) : null}
			{showIbanDocumentDialog ? (
				<UploadIBANDocument
					state={showIbanDocumentDialog}
					ibanRequestId={ibanRequestId}
					documentType={UserDocumentTypes.BorrowerIban}
					onClose={() => {
						setShowIbanDocumentDialog(false);
						setIbanRequestId(null);
						getBankDetails(pendingEditsRef.current);
						pendingEditsRef.current = null;
					}}
				/>
			) : null}
			{showIbanDialog ? (
				<DialogWrapper
					state={showIbanDialog}
					title={t('updateIban')}
					maxWidth="sm"
					onClose={() => setShowIbanDialog(false)}
					content={
						<Box className="pt-3">
							<TextField
								fullWidth
								label={t('iban')}
								value={ibanInput}
								onChange={(e) => setIbanInput(e.target.value)}
								variant="outlined"
							/>
						</Box>
					}
					actions={
						<Box className="flex gap-3">
							<FsButton variant="outline" onClick={() => setShowIbanDialog(false)}>
								{t('cancel')}
							</FsButton>
							<FsButton loading={ibanLoading} onClick={submitIbanUpdate}>
								{t('save')}
							</FsButton>
						</Box>
					}
				/>
			) : null}
		</Box>
	);
};
